<template>
    <div>
        <div
            class="modal fade"
            id="changeRegistrationCategoryModal"
            tabindex="-1"
            role="dialog"
            aria-labelledby="changeRegistrationCategoryModalLabel"
            aria-hidden="true"
        >
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title" id="changeRegistrationCategoryModalLabel">Change Race</h4>
                    <button
                        type="button"
                        class="close"
                        data-dismiss="modal"
                        aria-hidden="true"
                    >
                        ×
                    </button>
                </div>
                <div class="modal-body">
                    <div>
                        <p>
                            Please select the {{ registrationCategoryTerm }} to which you wish to
                            move from the options available in the drop-down, then click Change Race to confirm.
                        </p>
                        <form
                            ref="changeRegistrationCategoryForm"
                            method="post"
                            @submit="handleSubmit"
                        >
                            <!-- Radio buttons for team captain options -->
                            <div v-if="canMoveTeam" class="form-group">
                                <label class="control-label">Are you moving the team or just this participant?</label>
                                <div class="radio">
                                    <label>
                                        <input
                                            type="radio"
                                            name="move_entity_type"
                                            value="team"
                                            v-model="moveEntityType"
                                        >
                                        Move the entire team to different race
                                    </label>
                                </div>
                                <div class="radio">
                                    <label>
                                        <input
                                            type="radio"
                                            name="move_entity_type"
                                            value="individual"
                                            v-model="moveEntityType"
                                        >
                                        Move just this participant to a different race
                                    </label>
                                </div>
                            </div>

                            <div class="alert alert-danger" v-if="canMoveTeam && moveEntityType === 'individual'">Moving just the participant will keep the team in this {{registrationCategoryTerm}}
                                but move the participant out of this team.  Are you sure you want to continue? </div>

                            <div class="form-group required">
                                <label for="registration_category_id" class="control-label">
                                    {{ `Select the ${registrationCategoryTerm} you want to change to:` }}
                                </label>
                                <div v-if="isLoadingCategories" class="text-center">
                                    <i class="fa fa-spinner fa-spin"></i> Loading available options...
                                </div>
                                <group-select-input
                                    v-else
                                    id="registration_category_id"
                                    name="registration_category_id"
                                    :options="registrationCategories"
                                    value-key="value"
                                    label-key="text"
                                    :required="true"
                                    v-model="selectedRegistrationCategoryId"
                                ></group-select-input>
                            </div>
                            <p v-if="moveFee">
                                There will be an admin fee of {{ moveFeeFormatted }} for this move.
                            </p>
                            <button
                                type="submit"
                                class="btn-success btn btn-block btn-flat"
                                :disabled="!selectedRegistrationCategoryId || isSubmitting"
                            >
                                {{ `Change ${registrationCategoryTerm}` }}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
        <notification-container :notifications="notifications"/>
    </div>
    </div>
</template>

<script>
import GroupSelectInput from '../../Utility/Forms/InputFields/GroupSelect.vue';
import NotificationContainer from '../../Common/NotificationContainer.vue';
import notificationsMixin from '../../../mixins/notifications.js';

export default {
    name: 'UserMoveParticipantModal',
    mixins: [notificationsMixin],
    components: {
        GroupSelectInput,
        NotificationContainer,
    },
    props: {
        participant: {
            type: Object,
            required: true
        },
        userId:{
            type: Number|String,
            required:false,
            default:null
        },
        occasion: {
            type: Object,
            required: true
        },
        moveFeeFormatted: {
            type: String,
            required: false,
            default: null
        },
    },
    data() {
        return {
            registrationCategories: {},
            selectedRegistrationCategoryId: '',
            moveEntityType: 'team', // Default to team move
            isSubmitting: false,
            isLoadingCategories: false
        }
    },
    mounted() {
        // Listen for modal show event to load data when modal opens
        $('#changeRegistrationCategoryModal').on('show.bs.modal', () => {
            if (this.participant && this.isAllowedToMove()) {
                this.getMovableRegistrationCategoriesList();
            }
        });
    },
    computed: {
        registrationCategoryTerm() {
            if (this.occasion && this.occasion.calendar && this.occasion.calendar.registration_category_term) {
                return this.occasion.calendar.registration_category_term.toLowerCase();
            }

            return 'race';
        },
        moveFee() {
            if (this.occasion && this.occasion.registration_category_move_fee) {
                return this.occasion.registration_category_move_fee;
            }

            return 0;
        },
        canMoveTeam() {
            //No team
            if (!this.participant.team) {
                return false;
            }

            //Not a team ticket
            if(!this.participant.team_ticket || !this.participant.team.team_ticket){
                return false;
            }

            //Team has already moved
            if (this.participant.team.team_status === 'moved') {
                return false;
            }

            //Only the person who booked the team can move
            return this.userId == this.participant.team.user_id;
        }
    },
    watch: {
        // Re-fetch registration categories when transfer type changes
        moveEntityType() {
            if (this.canMoveTeam) {
                this.getMovableRegistrationCategoriesList();
            }
        }
    },
    beforeDestroy() {
        // Clean up event listener
        $('#changeRegistrationCategoryModal').off('show.bs.modal');
    },
    methods: {
        isAllowedToMove() {
            // This should match the PHP logic: $participant->isAllowedToMove()
            // For now, assuming the check is done before showing the modal
            return true;
        },

        async getMovableRegistrationCategoriesList() {
            this.isLoadingCategories = true;
            this.registrationCategories = {};
            this.selectedRegistrationCategoryId = '';

            let url = `/api/v0/registration_categories/${this.participant.occasion_id}/movable_list?rc_id=${this.participant.registration_category_id}`;

            if (this.canMoveTeam && this.moveEntityType === 'team') {
                url += `&team_ticket=true`;
            }

            try {
                const response = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.get(url, {
                        headers: {
                            'Authorization': jwtToken,
                            'Accept': 'application/json'
                        }
                    });
                });

                if (response.data && response.data.status === 'success' && response.data.data) {
                    this.registrationCategories = response.data.data;
                }
            } catch (error) {
                console.error('Failed to fetch movable registration categories:', error);
                this.showAlertMessage('Failed to load available categories. Please try again.', 'error', 5000);
            } finally {
                this.isLoadingCategories = false;
            }
        },

        handleSubmit(event) {
            event.preventDefault();

            if (!this.selectedRegistrationCategoryId || this.isSubmitting) {
                return;
            }

            this.isSubmitting = true;

            // Create form data
            const formData = new FormData();
            formData.append('registration_category_id', this.selectedRegistrationCategoryId);
            formData.append('move_type', 'withBalancePay');
            formData.append('move_by', 'user');

            // Determine endpoint based on entityType
            let endpoint;
            if (this.canMoveTeam && this.moveEntityType === 'team') {
                // Use teams endpoint for team moves
                endpoint = `/api/v0/teams/${this.participant.team_id}/change_registration_category`;
            } else {
                // Use participants endpoint for individual moves
                endpoint = `/api/v0/participants/change_registration_category/${this.participant.id}`;
            }

            // Submit via axios to the appropriate API endpoint
            this.$root.makeApiRequest(async (jwtToken) => {
                return await axios.post(
                    endpoint,
                    formData,
                    {
                        headers: {
                            'Authorization': jwtToken,
                            'Accept': 'application/json'
                        }
                    }
                );
            }).then(response => {
                // Handle success - API returns JSend format
                const result = response.data;

                console.log("resultresult", result)
                if (result.status === 'success') {
                    $('#changeRegistrationCategoryModal').modal('hide');

                    // Show success notification with message from API response
                    const successMessage = result.data.message || 'Registration category changed successfully!';
                    setTimeout(() => {
                        this.showAlertMessage(successMessage, 'success', 5000);
                    }, 300);

                    this.$emit('success', result.data);

                    // Redirect to basket with transaction identifier
                    if (result.data.transaction_identifier) {
                        setTimeout(() => {
                            window.location.href = `/basket?open=${result.data.transaction_identifier}`;
                        }, 1000);
                    } else {
                        // Fallback to basket without transaction identifier
                        setTimeout(() => {
                            window.location.href = '/basket';
                        }, 1000);
                    }
                } else {
                    // Handle API error response
                    const errorMessage = result.message ? result.message : 'Something went wrong. Please contact support.';
                    console.error('API returned error:', errorMessage);
                    this.showAlertMessage(errorMessage, 'error', 5000);
                    this.$emit('error', { message: errorMessage });
                }
            }).catch(error => {
                console.error('Failed to change registration category:', error);

                // Handle different types of errors
                let errorMessage = 'Something went wrong. Please contact support.';
                if (error.response && error.response.data) {
                    if (error.response.data.data && error.response.data.data.message) {
                        errorMessage = error.response.data.data.message;
                    } else if (error.response.data.message) {
                        errorMessage = error.response.data.message;
                    }
                }

                this.showAlertMessage(errorMessage, 'error', 5000);
                this.$emit('error', { message: errorMessage });
            }).finally(() => {
                this.isSubmitting = false;
            });
        }
    }
}
</script>



// WEBPACK FOOTER //
// src/Assets/components/Participants/Form/UserMoveParticipantModal.vue